import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Dialog,
    DialogContent,
    makeStyles,
    TextField,
    Theme,
    Typography
} from '@material-ui/core'
import React from 'react'
import ManageUnmappedDialogActions from './DialogActions'
import ManageUnmappedDialogTitle from './DialogTitle'

const useStyles = makeStyles((theme: Theme) => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: pxToRem(500),
        },
    },
    dialogContent: {
        padding: theme.spacing(0, 3, 2),
    },
    subtitle: {
        fontSize: pxToRem(22),
        fontWeight: 500,
        marginBottom: theme.spacing(2),
        color: '#304959',
    },
    formLabel: {
        minWidth: pxToRem(150),
        fontSize: pxToRem(14),
        marginBottom: theme.spacing(1),
        color: '#304959',
        alignItems: 'center',
        marginRight: theme.spacing(1),
        wrap: "nowrap"
    },
    formControlWrapper: {
        display: "flex",
        width: '100%',
        alignItems: "center"
    },
    textField: {
        marginBottom: theme.spacing(2),
        '& .MuiOutlinedInput-root': {
            '& fieldset': {
                borderColor: '#E0E0E0',
            },
            '&:hover fieldset': {
                borderColor: '#1899D4',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#1899D4',
            },
        },
        '& .MuiInputLabel-outlined': {
            fontSize: pxToRem(14),
        },
        '& .MuiOutlinedInput-input': {
            fontSize: pxToRem(16),
        },
    },
    textFieldLabel: {
        fontSize: pxToRem(14),
        alignItems: 'center',
    }
}))

/**
 * Validates family number format
 * Must be 6 digits for the main number, optionally followed by a decimal and up to 2 more digits
 * @param value - The family number to validate
 * @returns Whether the family number is valid
 */
const validateFamilyNumber = (value: string): boolean => {
    const isNumbersOnly = new RegExp(/[^0-9]+/, 'g')
    const familyNumber = value?.split('.')
    return (
        familyNumber[0]?.trim()?.replace(isNumbersOnly, '').length === 6 &&
        (!familyNumber[1] ||
            familyNumber[1]?.trim()?.replace(isNumbersOnly, '').length <= 2)
    )
}

/**
 * Form data for creating a new family
 */
export interface CreateNewFamilyFormData {
    /** The name of the section */
    sectionName: string
    /** The section number */
    sectionNumber: string
    /** The name of the product type */
    productTypeName: string
}

/**
 * Form errors for creating a new family
 */
interface CreateNewFamilyFormErrors {
    /** Error message for section name field */
    sectionName?: string
    /** Error message for section number field */
    sectionNumber?: string
    /** Error message for product type name field */
    productTypeName?: string
}

/**
 * Props for the CreateNewFamily component
 */
export interface CreateNewFamilyProps {
    /** Whether the dialog is open */
    open: boolean
    /** Callback when the dialog should be closed */
    onClose: () => void
    /** Callback when form is saved and user continues */
    onSaveAndContinue: (formData: CreateNewFamilyFormData) => void
}

/**
 * Dialog for creating a new family for an unmapped product.
 * 
 * Collects the required information to create a new MasterSpec family:
 * - Section Name
 * - Section Number  
 * - Product Type Name
 * 
 * Includes form validation to ensure all fields are filled before allowing submission.
 * 
 * @param props - The component props
 * @returns The CreateNewFamily component
 */
const CreateNewFamily: React.FC<CreateNewFamilyProps> = ({
    open,
    onClose,
    onSaveAndContinue,
}) => {
    const classes = useStyles()
    const [formData, setFormData] = React.useState<CreateNewFamilyFormData>({
        sectionName: '',
        sectionNumber: '',
        productTypeName: '',
    })
    const [errors, setErrors] = React.useState<CreateNewFamilyFormErrors>({})

    const handleInputChange = (field: keyof CreateNewFamilyFormData) => (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value,
        }))

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined,
            }))
        }
    }

    const handleFamilyNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // Only allow numbers and decimal points
        const tempValue = event.target.value?.replace(/[^0-9\.]/g, '') ?? ''
        const isFamilyNumberValid = validateFamilyNumber(tempValue)

        setFormData(prev => ({
            ...prev,
            sectionNumber: tempValue,
        }))

        // Set or clear validation error
        if (tempValue && !isFamilyNumberValid) {
            setErrors(prev => ({
                ...prev,
                sectionNumber: 'Invalid Family Number. Must be 6 digits, optionally followed by a decimal and up to 2 more digits.',
            }))
        } else if (errors.sectionNumber && isFamilyNumberValid) {
            setErrors(prev => ({
                ...prev,
                sectionNumber: undefined,
            }))
        }
    }

    const handleSaveAndContinue = () => {
        if (isFormValid) {
            onSaveAndContinue(formData)
        }
    }

    const handleClose = () => {
        setFormData({
            sectionName: '',
            sectionNumber: '',
            productTypeName: '',
        })
        setErrors({})
        onClose()
    }

    const isFormValid = React.useMemo(() => {
        // Check that all fields are filled and there are no validation errors
        const hasAllFields = Boolean(
            formData.sectionName.trim() &&
            formData.sectionNumber.trim() &&
            formData.productTypeName.trim()
        )

        const hasNoErrors = !errors.sectionName && !errors.sectionNumber && !errors.productTypeName
        const hasValidFamilyNumber = validateFamilyNumber(formData.sectionNumber)

        return hasAllFields && hasNoErrors && hasValidFamilyNumber
    }, [formData, errors])

    React.useEffect(() => {
        if (!open) {
            setFormData({
                sectionName: '',
                sectionNumber: '',
                productTypeName: '',
            })
            setErrors({})
        }
    }, [open])

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                if (reason !== 'backdropClick') {
                    handleClose()
                }
            }}
            className={classes.dialog}
            aria-labelledby="create-new-family-dialog-title"
        >
            <ManageUnmappedDialogTitle>
                Manage Unmapped Product
            </ManageUnmappedDialogTitle>
            <DialogContent className={classes.dialogContent}>
                <div className={classes.subtitle}>
                    Create a New Family
                </div>

                <Typography className={classes.formControlWrapper}>
                    <Typography className={classes.formLabel}>Section Name:</Typography>
                    <TextField
                        variant="outlined"
                        fullWidth
                        value={formData.sectionName}
                        onChange={handleInputChange('sectionName')}
                        className={classes.textField}
                        size="small"
                    />
                </Typography>

                <Typography className={classes.formControlWrapper}>
                    <Typography className={classes.formLabel}>Section Number:</Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        value={formData.sectionNumber}
                        onChange={handleFamilyNumberChange}
                        error={!!errors.sectionNumber}
                        helperText={errors.sectionNumber}
                        className={classes.textField}
                        size="small"
                    />
                </Typography>

                <Typography className={classes.formControlWrapper}>
                    <Typography className={classes.formLabel}>Product Type Name:</Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        value={formData.productTypeName}
                        onChange={handleInputChange('productTypeName')}
                        className={classes.textField}
                        size="small"
                    />
                </Typography>
            </DialogContent>
            <ManageUnmappedDialogActions
                onCancel={handleClose}
                onContinue={handleSaveAndContinue}
                disabled={!isFormValid}
            />
        </Dialog>
    )
}

export default CreateNewFamily